import { useCallback, useMemo } from 'react';

import { SageAuth, SageAuthLoginOptions } from './SageAuth';
import { useNativeAuthAvailability } from './SageAuthContext';
import { useNativeAuth } from './useNativeAuth';
import { useWebAuth } from './useWebAuth';
import { NativeAuthMethod } from 'src/native/nativeAuth';

/**
 * Which flow a login call is. SSO is distinguished by carrying a connection: the caller has
 * already resolved the customer's identity provider, and that flow must reach it through the
 * shell's browser rather than this web view.
 */
function methodFor(options?: SageAuthLoginOptions): NativeAuthMethod {
    return options?.connection ? 'sso' : 'ulp';
}

/** Routes each call to the shell only for methods it claims, otherwise to web, so flows go native
 * one at a time. Both backends always run (hooks can't be conditional); the unused one is inert. */
export function useSageAuth(): SageAuth {
    const { isNativeAuthAvailable, tenant, methods } = useNativeAuthAvailability();
    const isNativeAuthActive = isNativeAuthAvailable === true;
    // The flag and availability resolve asynchronously, and acting before they do picks an owner by
    // guess. Outside the shell both resolve synchronously, so this is never true in a browser.
    const negotiating = isNativeAuthAvailable === undefined;

    const web = useWebAuth();
    const native = useNativeAuth(tenant, isNativeAuthActive);

    const shellHandles = useCallback(
        (method: NativeAuthMethod) => isNativeAuthActive && methods.includes(method),
        [isNativeAuthActive, methods]
    );

    // Keyed on the backends' own stable callbacks, not the backends, so session updates do not
    // re-run the effects App keys on these.
    const { login: nativeLogin, sendOtp: nativeSendOtp, verifyOtp: nativeVerifyOtp } = native;
    const { login: webLogin, sendOtp: webSendOtp, verifyOtp: webVerifyOtp } = web;

    const login = useCallback(
        (options?: SageAuthLoginOptions) =>
            shellHandles(methodFor(options)) ? nativeLogin(options) : webLogin(options),
        [nativeLogin, shellHandles, webLogin]
    );

    const sendOtp = useCallback(
        (phoneNumber: string) =>
            shellHandles('otp') ? nativeSendOtp(phoneNumber) : webSendOtp(phoneNumber),
        [nativeSendOtp, shellHandles, webSendOtp]
    );

    const verifyOtp = useCallback(
        (phoneNumber: string, code: string) =>
            shellHandles('otp')
                ? nativeVerifyOtp(phoneNumber, code)
                : webVerifyOtp(phoneNumber, code),
        [nativeVerifyOtp, shellHandles, webVerifyOtp]
    );

    // A loading or failed shell owns only when web has no session: otherwise the user reads as
    // signed out, or a web session is handed to the shell's logout and survives it.
    const shellOwns =
        native.isAuthenticated || (!web.isAuthenticated && (native.isLoading || !!native.error));
    const owner = isNativeAuthActive && shellOwns ? native : web;

    return useMemo(
        () => ({
            ...owner,
            isLoading: negotiating || web.isLoading || (isNativeAuthActive && native.isLoading),
            login,
            sendOtp,
            verifyOtp,
        }),
        [
            isNativeAuthActive,
            login,
            native.isLoading,
            negotiating,
            owner,
            sendOtp,
            verifyOtp,
            web.isLoading,
        ]
    );
}
